import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import { CheckCircleOutline, EditOutlined, History, Refresh, CancelOutlined } from '@mui/icons-material';
import adminService from '../../services/adminService';

const formatDate = (value) => value ? new Date(value).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const AboutMeVerification = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('');
  const [editedText, setEditedText] = useState('');
  const [reason, setReason] = useState('');
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminService.getAboutMeReviewQueue({ page: 1, limit: 100 });
      setProfiles(response.data.profiles || []);
    } catch (error) {
      setNotice({ severity: 'error', text: error.response?.data?.message || 'Failed to load About Me review queue.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const startReview = (profile, action) => {
    setSelected(profile);
    setMode(action);
    setEditedText(profile.aboutMeModeration?.submittedText || '');
    setReason('');
  };

  const closeDialog = () => {
    if (saving) return;
    setSelected(null);
    setMode('');
    setHistory([]);
  };

  const review = async () => {
    setSaving(true);
    try {
      await adminService.reviewAboutMe(selected._id, {
        action: mode,
        editedText: mode === 'edit_and_approve' ? editedText : undefined,
        reason
      });
      setNotice({ severity: 'success', text: mode === 'reject' ? 'About Me rejected.' : 'About Me approved.' });
      setSelected(null);
      setMode('');
      await loadQueue();
    } catch (error) {
      setNotice({ severity: 'error', text: error.response?.data?.message || 'Review failed.' });
    } finally {
      setSaving(false);
    }
  };

  const showHistory = async (profile) => {
    setSelected(profile);
    setMode('history');
    setHistory([]);
    try {
      const response = await adminService.getAboutMeVerificationHistory(profile._id);
      setHistory(response.data.profile?.aboutMeVerificationHistory || []);
    } catch (error) {
      setNotice({ severity: 'error', text: error.response?.data?.message || 'History could not be loaded.' });
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' gap={2} mb={3}>
        <Box>
          <Typography variant='h4'>About Me Verification</Typography>
          <Typography color='text.secondary'>Approve profile descriptions before they become visible.</Typography>
        </Box>
        <Stack direction='row' gap={1} alignItems='center'>
          <Chip color='warning' label={profiles.length + ' pending'} />
          <Button variant='outlined' startIcon={<Refresh />} onClick={loadQueue}>Refresh</Button>
        </Stack>
      </Stack>
      {notice && <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}
      {loading ? <Box textAlign='center' py={8}><CircularProgress /></Box> : profiles.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <CheckCircleOutline color='success' sx={{ fontSize: 54 }} />
          <Typography variant='h6' mt={1}>Review queue is clear</Typography>
          <Typography color='text.secondary'>No pending About Me submissions.</Typography>
        </Paper>
      ) : <Stack gap={2}>{profiles.map((profile) => (
        <Paper key={profile._id} sx={{ p: { xs: 2, md: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2} justifyContent='space-between'>
            <Box flex={1}>
              <Stack direction='row' gap={1} alignItems='center' flexWrap='wrap'>
                <Typography variant='h6'>{profile.user?.name || profile.name || 'Unnamed user'}</Typography>
                <Chip size='small' color='warning' label='Pending verification' />
              </Stack>
              <Typography variant='body2' color='text.secondary'>
                {(profile.user?.email || 'No email') + ' · ' + formatDate(profile.aboutMeModeration?.submittedAt)}
              </Typography>
              <Box display='grid' gridTemplateColumns={{ xs: '1fr', lg: '1fr 1fr' }} gap={2} mt={2}>
                <Paper variant='outlined' sx={{ p: 2, bgcolor: '#f8fafc' }}>
                  <Typography variant='caption' fontWeight={700}>CURRENT APPROVED</Typography>
                  <Typography mt={1}>{profile.aboutMe || 'Nothing approved yet.'}</Typography>
                </Paper>
                <Paper variant='outlined' sx={{ p: 2, bgcolor: '#fff7ed', borderColor: '#fed7aa' }}>
                  <Typography variant='caption' fontWeight={700}>SUBMITTED DRAFT</Typography>
                  <Typography mt={1}>{profile.aboutMeModeration?.submittedText}</Typography>
                </Paper>
              </Box>
            </Box>
            <Stack minWidth={{ md: 190 }} gap={1}>
              <Button color='success' variant='contained' startIcon={<CheckCircleOutline />} onClick={() => startReview(profile, 'approve')}>Approve</Button>
              <Button variant='outlined' startIcon={<EditOutlined />} onClick={() => startReview(profile, 'edit_and_approve')}>Edit & Approve</Button>
              <Button color='error' variant='outlined' startIcon={<CancelOutlined />} onClick={() => startReview(profile, 'reject')}>Reject</Button>
              <Button startIcon={<History />} onClick={() => showHistory(profile)}>History</Button>
            </Stack>
          </Stack>
        </Paper>
      ))}</Stack>}

      <Dialog open={Boolean(selected && mode && mode !== 'history')} onClose={closeDialog} fullWidth maxWidth='sm'>
        <DialogTitle>{mode === 'reject' ? 'Reject About Me' : mode === 'edit_and_approve' ? 'Edit and Approve' : 'Approve About Me'}</DialogTitle>
        <DialogContent>
          {mode === 'edit_and_approve' ? <TextField autoFocus fullWidth multiline minRows={5} value={editedText}
            onChange={(event) => setEditedText(event.target.value)} inputProps={{ maxLength: 255 }}
            helperText={editedText.length + '/255 characters'} sx={{ mt: 1 }} />
            : <Paper variant='outlined' sx={{ p: 2, mt: 1 }}>{selected?.aboutMeModeration?.submittedText}</Paper>}
          {mode === 'reject' && <TextField autoFocus required fullWidth multiline minRows={3} label='Reason for rejection'
            value={reason} onChange={(event) => setReason(event.target.value)} sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant='contained' color={mode === 'reject' ? 'error' : 'success'} onClick={review}
            disabled={saving || (mode === 'reject' && !reason.trim()) || (mode === 'edit_and_approve' && !editedText.trim())}>
            {saving ? 'Saving…' : mode === 'reject' ? 'Reject' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selected && mode === 'history')} onClose={closeDialog} fullWidth maxWidth='md'>
        <DialogTitle>Verification History</DialogTitle>
        <DialogContent>
          {history.length === 0 ? <Typography color='text.secondary' py={3}>No history found.</Typography> : (
            <Stack divider={<Divider flexItem />}>{[...history].reverse().map((entry) => (
              <Box key={entry._id || entry.changedAt} py={2}>
                <Stack direction='row' justifyContent='space-between'>
                  <Chip size='small' label={entry.action.replaceAll('_', ' ')} />
                  <Typography variant='caption'>{formatDate(entry.changedAt)}</Typography>
                </Stack>
                <Typography variant='body2' mt={1}>{'Changed by ' + (entry.actorAdmin?.name || entry.actorUser?.name || entry.actorType)}</Typography>
                {entry.reason && <Alert severity='warning' sx={{ my: 1 }}>{entry.reason}</Alert>}
                <Box display='grid' gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={1} mt={1}>
                  <Paper variant='outlined' sx={{ p: 1.5 }}><b>Before:</b> {entry.previousText || 'Empty'}</Paper>
                  <Paper variant='outlined' sx={{ p: 1.5 }}><b>After:</b> {entry.newText || 'Empty'}</Paper>
                </Box>
              </Box>
            ))}</Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={closeDialog}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default AboutMeVerification;
